import { currentUserQuery } from '@/api/user/userQueries';
import { AuthHeader } from '@/components/auth/AuthHeader';
import { Spinner } from '@/components/common/Spinner';
import { rootRoute } from '@/routes/rootRoute';
import { routes } from '@/routes/routes';
import {
  loginSearchSchema,
  registerSearchSchema,
  resetPasswordSearchSchema,
} from '@/validation/authValidator';
import { useQuery } from '@tanstack/react-query';
import {
  Outlet,
  createRoute,
  lazyRouteComponent,
  redirect,
  useNavigate,
} from '@tanstack/react-router';
import { useEffect } from 'react';

export const AuthLayout = () => {
  const navigate = useNavigate();
  const { data: user, isLoading } = useQuery({
    ...currentUserQuery,
    retry: false,
  });

  useEffect(() => {
    if (!isLoading && user?.data) {
      navigate({ to: routes.dashboard.index });
    }
  }, [isLoading, user, navigate]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner className="size-8" />
      </div>
    );
  }

  return (
    <main className="flex min-h-screen flex-col">
      <AuthHeader />

      <div className="flex flex-1 flex-col justify-center">
        <Outlet />
      </div>
    </main>
  );
};

export const authLayoutRoute = createRoute({
  id: '_auth-layout',
  getParentRoute: () => rootRoute,
  component: AuthLayout,
});

export const loginRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: routes.auth.login,
  validateSearch: (search) => loginSearchSchema.parse(search),
  component: lazyRouteComponent(() => import('./LoginPage'), 'LoginPage'),
});

export const registerRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: routes.auth.register,
  validateSearch: (search) => registerSearchSchema.parse(search),
  component: lazyRouteComponent(() => import('./RegisterPage'), 'RegisterPage'),
});

export const forgotPasswordRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: routes.auth.forgotPassword,
  component: lazyRouteComponent(
    () => import('./ForgotPasswordPage'),
    'ForgotPasswordPage',
  ),
});

export const resetPasswordRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: routes.auth.resetPassword,
  validateSearch: (search) => resetPasswordSearchSchema.parse(search),
  beforeLoad: ({ search }) => {
    const hasResetLink = search.token && search.email;
    const hasInvitation = search.invitation && search.email;
    if (!hasResetLink && !hasInvitation) {
      throw redirect({
        to: routes.auth.login,
      });
    }
  },
  component: lazyRouteComponent(
    () => import('./ResetPasswordPage'),
    'ResetPasswordPage',
  ),
});

export const authRoutes = authLayoutRoute.addChildren([
  loginRoute,
  registerRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
]);
